<template>
  <div class="access-log">
    <confirmation-modal
      v-if="showExportConfirmation"
      :confirmation-type="confirmModal.type"
      :title="confirmModal.title"
      :description="exportConfirmationMessage"
      :button-text="confirmModal.button"
      @cancel="toggleExportConfirmation"
      @confirm="fetchCSVLogs"
    />
    <div class="page-topbar m-0">
      <div class="page-topbar__title">
        Physical Access Log
      </div>
      <div class="log-page-actions">
        <button
          v-if="accessLogs.length > 0"
          id="export-logs-button"
          class="btn mr-2"
          @click="toggleExportConfirmation"
        >
          Export Logs
        </button>
      </div>
    </div>
    <div class="log-actions">
      <TimePeriodDropdown v-bind.sync="timePeriodFilter" />
      <span class="ml-1">
        Showing logs from
        <strong>{{ timePeriodFilter.start | date }}</strong>
        to <strong>{{ timePeriodFilter.end | date }}</strong>.
      </span>
    </div>
    <div class="log-topbar">
      <pagination
        :loading="loading"
        :page.sync="logPaginationParams.page"
        :per-page.sync="logPaginationParams.perPage"
        :total-rows.sync="totalRows"
        :rows-per-page-label="'Rows per page'"
      />
      <div class="log-item log-item__header">
        <span />
        <span>Date</span>
        <span>User</span>
        <span>Door</span>
        <span>Authentication</span>
      </div>
    </div>
    <template v-if="accessLogs.length > 0">
      <template v-for="(logItem, index) in accessLogs">
        <div
          :key="index"
          class="log-item-container"
          :class="{ 'log-item-container__expanded': logItem.isExpanded }"
        >
          <div
            :id="'log-item-' + index"
            :key="index"
            :data-testid="logItem.dataTestId"
            class="log-item"
            :class="{ 'log-item__expanded': logItem.isExpanded }"
            @click="toggleExpandLogItem(logItem)"
          >
            <span
              v-tooltip="{ content: accessEventStatus(logItem) }"
              v-html="getAccessEventVerificationIcon(logItem)"
            />
            <span :title="logItem.createdAt | date">
              {{ logItem.initiatedAt | date }}
            </span>
            <span v-tooltip="{ content: logItem.username }">
              {{ logItem.name }}
            </span>
            <span>{{ logItem.deviceName }}</span>
            <span>{{ logItem.authenticationDescription }}</span>
            <span
              v-show="hasAccessEventDetails(logItem)"
              class="dropdown-button-chevron"
              :class="{ 'dropdown-button-chevron__expanded': logItem.isExpanded }"
              v-html="chevronDown"
            />
          </div>
          <div :data-testid="'log-event-item-container-' + index">
            <template v-if="logItem.isExpanded">
              <template
                v-for="(subEventItem, subEventIndex) in formattedAccessEventDetails(logItem)"
              >
                <div
                  :id="'log-event-item-' + subEventIndex"
                  :key="subEventIndex"
                  :data-testid="'log-event-item-' + subEventIndex"
                  class="log-event-item"
                >
                  <!-- placeholder span to align items with parent row -->
                  <span />
                  <span
                    class="log-event-item log-event-item-icon"
                    v-html="getSubEventIcon(subEventItem)"
                  />
                  <div class="log-event-item-status">
                    <span :title="subEventItem.status">
                      <b> {{ subEventItem.status }} </b>
                    </span>
                    <span class="log-event-item-status__timestamp"> {{ subEventItem.time }}</span>
                  </div>
                  <span>
                    {{ subEventItem.name }}
                  </span>
                  <span>
                    {{ subEventItem.credentialType }}
                  </span>
                  <!-- placeholder span to align items with parent row -->
                  <span />
                </div>
              </template>
            </template>
          </div>
        </div>
      </template>
    </template>
    <template v-else>
      <div class="empty-state mt-2">
        <template v-if="loading">
          Loading logs...
        </template>
        <template v-else>
          No access events for this time period.
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import Pagination from '@/components/pagination.vue'
import TimePeriodDropdown from '@/components/timePeriodDropdown.vue'
import ConfirmationModal from '@/components/confirmationModal.vue'
import accessEventQueries from '@/graphql/accessEvents.js'
import feather from 'feather-icons'
import moment from 'moment'
import constants from '@/global/constants.js'
import alertIcon from '@/assets/alert.svg'
import doorOpenIcon from '@/assets/door-opened.svg'
import doorClosedIcon from '@/assets/door-closed.svg'
import doorHeldOpenIcon from '@/assets/door-held-open.svg'

export default {
  name: 'AccessLog',
  components: {
    Pagination,
    TimePeriodDropdown,
    ConfirmationModal,
  },
  mixins: [accessEventQueries],
  data() {
    return {
      showExportConfirmation: false,
      loading: false,
      accessLogs: [],
      totalRows: 0,
      timePeriodFilter: {
        start: null,
        end: null,
      },
      logPaginationParams: {
        page: 1,
        perPage: 50,
      },
      confirmModal: {
        type: 'friendly',
        title: 'Export Access Logs',
        button: 'Export',
      },
      refreshIcon: feather.icons['refresh-cw'].toSvg(),
      chevronDown: feather.icons['chevron-down'].toSvg(),
    }
  },
  computed: {
    selectedDatesFormated() {
      if (this.timePeriodFilter.start && this.timePeriodFilter.end) {
        return {
          fromDate: moment(this.timePeriodFilter.start).utc().format(),
          toDate: moment(this.timePeriodFilter.end).utc().format(),
        }
      }
      return null
    },
    exportConfirmationMessage() {
      if (this.loading) {
        return 'Exporting Logs...'
      }
      const {start, end} = this.timePeriodFilter
      const _formatString = 'ddd. MMM DD YYYY, h:mm a'
      const _startFormatted = moment(start).format(_formatString)
      const _endFormatted = moment(end).format(_formatString)
      return `Export Logs from ${_startFormatted} to ${_endFormatted}. All Timestamps in CSV are in UTC`
    },
  },
  watch: {
    logPaginationParams: {
      deep: true,
      handler() {
        this.fetchAccessEvents()
      },
    },
    selectedDatesFormated: {
      deep: true,
      /*
       * Don't fire on first change
       * Make sure when a date is changed, to go back to the first page
       * to avoid empty data on a phantom page
       */
      handler(newVal, oldVal) {
        if (oldVal !== null) {
          this.logPaginationParams.page = 1
          this.fetchAccessEvents()
        }
      },
    },
  },
  created() {
    this.fetchAccessEvents()
  },
  methods: {
    hasAccessEventDetails(accessEvent) {
      return (
        (accessEvent.transactions && accessEvent.transactions.length > 0)
        || (accessEvent.deviceEvents && accessEvent.deviceEvents.length > 0)
      )
    },
    toggleExpandLogItem(accessEvent) {
      accessEvent.isExpanded = !accessEvent.isExpanded
    },
    toggleExportConfirmation() {
      this.showExportConfirmation = !this.showExportConfirmation
    },
    accessEventStatus(accessEvent) {
      const {isAccessGranted, accessMode} = accessEvent

      if (accessMode === constants.accessModeEnum.ForcedOpen) {
        return 'Door opened by unknown user'
      }

      if (isAccessGranted === null) {
        return 'Pending/Incomplete'
      }

      return this.determineStatusBasedOnAccessGranted(isAccessGranted, accessMode)
    },
    determineStatusBasedOnAccessGranted(isAccessGranted, accessMode) {
      if (!isAccessGranted) {
        return 'Denied'
      }

      return this.getStatusFromMode(accessMode)
    },
    getStatusFromMode(accessMode) {
      switch (accessMode) {
      case constants.accessModeEnum.AccessRequest:
      case constants.accessModeEnum.MultiParty:
      case constants.accessModeEnum.MultiPartyAccessRequest:
        return 'Accepted'
      case constants.accessModeEnum.Otp:
        return 'Remotely Authorized'
      default:
        return 'Unknown'
      }
    },
    /*
     * We only format the accessEvent details when an access event row is expanded.
     * This is done to avoid unncessary pre-processing after fetching the data
     */
    formattedAccessEventDetails(accessEvent) {
      const options = {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      }
      const accessSubEvents = [
        ...this.processTransactions(accessEvent.transactions, options),
        ...this.processDeviceEvents(accessEvent.deviceEvents, options),
      ]
      return accessSubEvents.sort((a, b) => {
        return new Date(a.time) - new Date(b.time)
      })
    },
    processTransactions(transactions = [], options) {
      return transactions.map((transaction) => {
        return {
          ...transaction,
          status: `Credential ${this.getTransactionStatus(transaction)}`,
          name: this.formatName(this.getNames([transaction], null)),
          time: this.formatTime(transaction.createdAt, options),
          credentialType: this.getCredentialLabel(transaction),
          eventType: constants.accessEventTypeEnum.Transaction,
        }
      })
    },
    processDeviceEvents(deviceEvents = [], options) {
      return deviceEvents.map((event) => {
        return {
          ...event,
          status: this.getDeviceStatus(event),
          time: this.formatTime(event.deviceEventAt, options),
          name: this.formatAdminList(this.getNames(null, [event])),
          credentialType: '',
          eventType: constants.accessEventTypeEnum.DeviceEvent,
        }
      })
    },
    getTransactionStatus(transaction) {
      if (transaction.isComplete && !transaction.isValid) {
        return 'Declined'
      }
      if (transaction.isComplete && transaction.isValid) {
        return 'Approved'
      }
      return 'Pending'
    },
    getCredentialLabel(transaction) {
      const type = constants.transactionCredentialType.find((item) => {
        return item.value === transaction.credentialType
      })
      return type ? type.label + (transaction.requireStepUp ? ' + Mobile' : '') : 'Unknown'
    },
    getDeviceStatus(event) {
      const status = constants.deviceEvent.find((item) => {
        return item.value === event.eventType
      })
      return status ? status.label : 'Unknown'
    },
    formatTime(time, options) {
      return time ? new Date(time).toLocaleString('en-US', options) : ''
    },
    formatName(names) {
      return names.length > 2 ? `${names[0]} + 1` : names[0]
    },
    formatAdminList(admins) {
      return admins.length > 2 ? admins.slice(0, 2).join(', ') + '...' : admins.join(', ')
    },
    getAccessEventVerificationIcon(accessEvent) {
      const {accessMode, isAccessGranted} = accessEvent
      const green = '#7fbf42'
      const orange = '#ffa500'

      if (accessMode === constants.accessModeEnum.ForcedOpen) {
        return `<img src='${alertIcon}' data-testid='alert-icon'>`
      }

      if (isAccessGranted === null) {
        return feather.icons.clock.toSvg({color: orange})
      }

      return isAccessGranted
        ? feather.icons['check-circle'].toSvg({color: green})
        : feather.icons['x-octagon'].toSvg()
    },
    getSubEventIcon(subEvent) {
      const {eventType, status} = subEvent
      if (eventType === constants.accessEventTypeEnum.Transaction) {
        return this.getTransactionIcon(subEvent)
      }
      return this.getDeviceEventIcon(status)
    },
    getTransactionIcon(transaction) {
      const {isComplete, isValid} = transaction
      const orange = '#ffa500'
      const green = '#7fbf42'
      if (!isComplete) {
        return feather.icons.clock.toSvg({color: orange})
      }
      return isComplete && isValid
        ? feather.icons['check-circle'].toSvg({color: green})
        : feather.icons['x-octagon'].toSvg()
    },
    getDeviceEventIcon(status) {
      const iconMap = {
        [constants.deviceEventTypeEnum.DoorOpen]: `<img src='${doorOpenIcon}'>`,
        [constants.deviceEventTypeEnum.DoorHeldOpen]: `<img src='${doorHeldOpenIcon}'>`,
        [constants.deviceEventTypeEnum.DoorForcedOpen]: `<img src='${alertIcon}'>`,
        [constants.deviceEventTypeEnum.DoorClose]: `<img src='${doorClosedIcon}'>`,
        [constants.deviceEventTypeEnum.Otp]: feather.icons['alert-circle'].toSvg({
          color: '#ffa500',
        }),
      }

      if (iconMap[status]) {
        return iconMap[status]
      }
      throw new Error(`Unknown device event status: ${status}`)
    },
    async fetchAccessEvents() {
      this.loading = true
      try {
        const {accessEvents, totalRows} = await this.listAccessEventsQuery(
          this.logPaginationParams,
          this.selectedDatesFormated,
        )
        this.loading = false
        this.totalRows = totalRows
        this.accessLogs = accessEvents.map((event) => {
          /*
           * For a TOTP event, a list of device admin name(s) will be returned
           * that we update to show on the front end
           */
          const user = this.getNames(event.transactions, event.deviceEvents)
          const multiPartyUserThreshold = 1
          return {
            ...event,
            name: user.length > multiPartyUserThreshold ? user[0] + ' + 1' : user[0],
            authenticationDescription: this.getAuthDescription(event),
            isExpanded: false,
          }
        })
      } catch (error) {
        this.loading = false
        this.displayAlert('fail', 'Unable to retrieve access logs.')
        console.log('Unable to retrieve access logs.', error)
      }
    },
    async fetchCSVLogs() {
      this.loading = true
      try {
        const _csvString = await this.getAccessEventsAsCSV(this.selectedDatesFormated)
        this.loading = false
        this.exportCSVLogs(_csvString)
        this.toggleExportConfirmation()
      } catch (error) {
        this.loading = false
        this.displayAlert('fail', 'An error occurred while trying to export logs.')
        console.log('Unable to export logs.', error)
      }
    },
    /*
     * In order to avoid pop-up blockers, and to set a file name
     * create a hidden link in DOM node, encode with encodeURIComponent to
     * support larger file sizes and set its download attribute as follows
     */
    exportCSVLogs(csvString) {
      const _csvDataType = 'data:text/csv;charset=utf-8,'
      const _encodedCsvUri = encodeURIComponent(csvString)
      const _href = _csvDataType + _encodedCsvUri
      const _todayFormatted = moment().format('YYYYMMDD')
      const _fileName = `BioConnect_AccessLogs_${_todayFormatted}.csv`
      const _anchor = document.createElement('a')
      _anchor.dataset.cy = 'download-csv'
      _anchor.href = _href
      _anchor.target = '_blank'
      _anchor.download = _fileName
      document.body.appendChild(_anchor)
      _anchor.click()
    },
    getAuthDescription(event) {
      const accessMode = this.findAccessMode(event.accessMode)
      if (!accessMode) {
        return 'Unknown'
      }
      if (accessMode.value === 'AccessRequest') {
        return this.describeAccessRequest(event)
      }
      return accessMode.label
    },
    findAccessMode(accessModeValue) {
      return constants.accessEventAccessModeType.find((item) => {
        return item.value === accessModeValue
      })
    },
    describeAccessRequest(event) {
      if (!event.transactions || event.transactions.length === 0) {
        return 'Access Request without transaction'
      }

      const [{credentialType, requireStepUp}] = event.transactions
      const transactionTypeItem = this.findTransactionType(credentialType)
      return transactionTypeItem
        ? `${transactionTypeItem.label}${requireStepUp ? ' + Mobile' : ''}`
        : 'Unknown'
    },
    findTransactionType(credentialType) {
      return constants.transactionCredentialType.find((item) => {
        return item.value === credentialType
      })
    },
    getNames(transactions, deviceEvents) {
      let names = ['Unknown']
      if (transactions && transactions.length > 0) {
        names = this.getTransactionNames(transactions)
      } else if (deviceEvents && deviceEvents.length > 0) {
        names = this.getDeviceEventNames(deviceEvents)
      }
      return names
    },
    getTransactionNames(transactions) {
      const names = []
      transactions.forEach((transaction) => {
        const {username, firstName, lastName} = transaction
        if (username && username === 'bypassUser') {
          names.push('Unassigned User')
        } else if (firstName && lastName) {
          names.push(`${firstName} ${lastName}`)
        } else {
          names.push('Unknown')
        }
      })

      return names
    },
    getDeviceEventNames(deviceEvents) {
      // Default unknown since additional data will not be present for non-otp device events
      let names = ['Unknown']
      deviceEvents.forEach((event) => {
        const {additionalData} = event
        if (additionalData && additionalData.admins) {
          names = []
          const {admins} = additionalData
          admins.forEach((admin) => {
            names.push(`${admin.firstName} ${admin.lastName}`)
          })
        }
      })

      return names
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/scss/_tables.scss";
.log {
  &-actions {
    @include flex-display();
    @include flex-align-items(center);
    margin-top: 2rem;
    font-size: $small-font;
    font-style: italic;
    color: $grey-text;
  }
  &-page-actions {
    @include flex-display();
    @include flex-align-items(center);
  }
  &-topbar {
    position: sticky;
    top: 0;
    padding-top: 1px; // Using pagination component margin
    background: $white;
    z-index: 1; // Keep the element above others on the page
  }
  &-table {
    width: 100%;
    @media #{$mobile} {
      overflow-x: auto;
    }
  }
  &-item {
    @include grid-table-item();
    grid-template-columns: 0.3fr 2fr 2fr 2fr 1fr 0.3fr;
    &__header {
      @include grid-table-header();
    }
    &__expanded {
      font-weight: bold;
    }
    &-container {
      &__expanded {
        border: 1px solid $light-grey;
        box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.1);
      }
    }
  }
  &-event {
    &-table {
      width: 100%;
      @media #{$mobile} {
        overflow-x: auto;
      }
    }
    &-item {
      @include grid-table-item();
      // Very odd spacing, but trust me it took a lot of trial and error to get everything to align
      grid-template-columns: 0.3fr 0.3fr 1.73fr 4fr 1fr 0.3fr;
      grid-gap: 0;
      &-icon {
        cursor: default !important; // Prevents cursor changing to a hand when hovering over icon
      }
      &-status {
        margin-left: 10px;
        display: flex;
        flex-direction: column;
        &__timestamp {
          font-size: small;
        }
      }
    }
  }
}

/*
 * The dropdown button in this component is slightly different than what is defined
 * in the library. That's why we some properties have been duplicated and added to
 * fit the functionality on this component
 */
.dropdown-button-chevron {
  @include flex-display();
  @include flex-align-items(center);
  @include flex-justify-content(space-between);
  position: relative;
  display: inline-block;
  background: none;
  text-transform: uppercase;
  font-size: $default-font;
  letter-spacing: 1px;
  font-weight: 600;
  color: $black;
  outline: none;
  transition: transform 0.3s ease-in-out;
  cursor: pointer;
  &__expanded {
    transform: rotate(180deg);
  }
}
</style>
